<img src="assets/images/Logo_Alphalearn_outlines_fluid_animated.svg" alt="AlphaLearn" class="auth-logo" />

<div class="auth-toggle">
  <a class="toggle-btn active" routerLink="../login">Login</a>
  <a class="toggle-btn" routerLink="../register">Register</a>
</div>

<nb-alert
  *ngIf="showMessages.error && errors?.length && !submitted"
  outline="danger"
  role="alert"
>
  <p class="alert-title"><b>Oh snap!</b></p>
  <ul class="alert-message-list">
    <li *ngFor="let error of errors" class="alert-message">{{ error }}</li>
  </ul>
</nb-alert>

<!-- Sign-in refused because the registration was never confirmed. This is
     the one login failure with a self-service fix, so it gets a button. -->
<nb-alert *ngIf="unconfirmedEmail" outline="warning" role="alert">
  <p class="alert-title">
    <b>{{ "pages.login.unconfirmed.title" | translate }}</b>
  </p>
  <p class="alert-message">
    {{ "pages.login.unconfirmed.description" | translate }}
  </p>

  <button
    *ngIf="!resendSuccess"
    nbButton
    status="warning"
    size="small"
    [nbSpinner]="resendLoading"
    nbSpinnerStatus="control"
    [disabled]="resendLoading"
    (click)="resendConfirmation()"
  >
    {{ "pages.login.unconfirmed.resendButton" | translate }}
  </button>

  <p class="alert-message" *ngIf="resendSuccess">
    {{ "pages.login.unconfirmed.resendSuccess" | translate }}
  </p>
  <p class="alert-message status-danger" *ngIf="resendError">
    {{ resendError | translate }}
  </p>
</nb-alert>

<nb-alert
  *ngIf="showMessages.success && messages?.length && !submitted"
  outline="success"
  role="alert"
>
  <p class="alert-title"><b>Hooray!</b></p>
  <ul class="alert-message-list">
    <li *ngFor="let message of messages" class="alert-message">
      {{ message }}
    </li>
  </ul>
</nb-alert>

<form (ngSubmit)="login()" #form="ngForm" aria-labelledby="title">
  <ng-container *ngIf="!twoFactorRequired">
  <div class="form-control-group">
    <input
      nbInput
      fullWidth
      [(ngModel)]="user.email"
      #email="ngModel"
      name="email"
      id="input-email"
      pattern=".+@.+\..+"
      placeholder="Email address"
      fieldSize="large"
      autofocus
      [status]="email.dirty ? (email.invalid ? 'danger' : 'success') : 'basic'"
      [required]="getConfigValue('forms.validation.email.required')"
      [attr.aria-invalid]="email.invalid && email.touched ? true : null"
    />
    <ng-container *ngIf="email.invalid && email.touched">
      <p class="caption status-danger" *ngIf="email.errors?.['pattern']">
        Email should be the real one!
      </p>
    </ng-container>
  </div>

  <div class="form-control-group">
    <input
      nbInput
      fullWidth
      [(ngModel)]="user.password"
      #password="ngModel"
      name="password"
      type="password"
      id="input-password"
      placeholder="Password"
      fieldSize="large"
      [status]="
        password.dirty ? (password.invalid ? 'danger' : 'success') : 'basic'
      "
      [required]="getConfigValue('forms.validation.password.required')"
      [attr.aria-invalid]="password.invalid && password.touched ? true : null"
    />
    <!-- No minlength/maxlength here on purpose. `forms.validation.password`
         is the policy for SETTING a password (register / reset / change —
         12 chars since 2026-09-21, matching al-user-management's
         password_policy.py). Login only checks credentials the server
         already accepted; binding the policy here locked out every
         existing account with a shorter password (2026-09-22). -->
    <ng-container *ngIf="password.invalid && password.touched">
      <p class="caption status-danger" *ngIf="password.errors?.['required']">
        Password is required!
      </p>
    </ng-container>
  </div>

  <div class="forgot-password-row">
    <a class="forgot-password caption-2" routerLink="../request-password">Forgot Password?</a>
  </div>
  </ng-container>

  <!-- Second step. The password was already accepted; only the code is
       missing. Email and password are still held in `user` and get posted
       again together with it, so nothing has to be retyped. -->
  <ng-container *ngIf="twoFactorRequired">
    <p class="caption twofa-hint">
      Enter the 6-digit code from your authenticator app.
    </p>

    <div class="form-control-group" *ngIf="!useRecoveryCode">
      <input
        nbInput
        fullWidth
        [(ngModel)]="user.totp_code"
        name="totp_code"
        id="input-totp"
        inputmode="numeric"
        autocomplete="one-time-code"
        maxlength="7"
        placeholder="123 456"
        fieldSize="large"
        autofocus
        required
      />
    </div>

    <div class="form-control-group" *ngIf="useRecoveryCode">
      <input
        nbInput
        fullWidth
        [(ngModel)]="user.recovery_code"
        name="recovery_code"
        id="input-recovery"
        autocomplete="off"
        placeholder="ABCDE-FGHJK"
        fieldSize="large"
        autofocus
        required
      />
      <p class="caption">Each recovery code works once.</p>
    </div>

    <div class="forgot-password-row">
      <a class="forgot-password caption-2" (click)="toggleRecoveryCode()">
        {{ useRecoveryCode ? 'Use my authenticator app' : "I can't reach my app" }}
      </a>
      <a class="forgot-password caption-2" (click)="cancelSecondFactor()">Start over</a>
    </div>
  </ng-container>

  <app-turnstile
    *ngIf="!twoFactorRequired"
    (tokenReceived)="onCaptureTokenReceived($event)"
    [onRenew]="renewToken$"
  ></app-turnstile>

  <div class="form-control-group accept-group">
    <nb-checkbox
      name="rememberMe"
      [(ngModel)]="user.rememberMe"
      *ngIf="rememberMe"
      >Remember me</nb-checkbox
    >
  </div>

  <button
    nbButton
    fullWidth
    status="primary"
    size="large"
    [disabled]="submitted || !form.valid || (!twoFactorRequired && !captureToken)"
    [class.btn-pulse]="submitted"
    [nbSpinner]="!twoFactorRequired && !captureToken"
  >
    {{ twoFactorRequired ? 'Verify' : 'Log In' }}
  </button>

  <p class="engineered-tag">Engineered in Berlin by Alphabees</p>
</form>

<section
  *ngIf="socialLinks && socialLinks.length > 0"
  class="links"
  aria-label="Social sign in"
>
  or enter with:
  <div class="socials">
    <ng-container *ngFor="let socialLink of socialLinks">
      <a
        *ngIf="socialLink.link"
        [routerLink]="socialLink.link"
        [attr.target]="socialLink.target"
        [attr.class]="socialLink.icon"
        [class.with-icon]="socialLink.icon"
      >
        <nb-icon
          *ngIf="socialLink.icon; else title"
          [icon]="socialLink.icon"
        ></nb-icon>
        <ng-template #title>{{ socialLink.title }}</ng-template>
      </a>
      <a
        *ngIf="socialLink.url"
        [attr.href]="socialLink.url"
        [attr.target]="socialLink.target"
        [attr.class]="socialLink.icon"
        [class.with-icon]="socialLink.icon"
      >
        <nb-icon
          *ngIf="socialLink.icon; else title"
          [icon]="socialLink.icon"
        ></nb-icon>
        <ng-template #title>{{ socialLink.title }}</ng-template>
      </a>
    </ng-container>
  </div>
</section>

